import React, {
  useCallback,
  useEffect,
  useState,
} from "react";
 
import Onboarding from "../pages/Onboarding";
import OnboardingWrapper from './OnboardingWrapper'; 
import {
  useNavigate,
  useLocation,
  Link,
} from "react-router-dom";

import {
  motion,
  AnimatePresence,
  useDragControls,
} from "framer-motion";

import {
  Menu,
  Bell,
  ChevronRight,
  Clock,
  ChevronUp,
  ChevronDown,
  ArrowLeft,
  Sparkles,
  Download,
  Bug,
  Smartphone,
  CheckCircle2,
} from "lucide-react";

import {
  api,
  StorageKeys,
} from "../lib/api";

import {
  storage,
} from "../lib/storage";

import {
  MENU,
} from "./nav";

import {
  GurSetuWordmark,
} from "./GurSetuWordmark";

import {
  useI18n,
} from "../lib/i18n";
import SacredPageTransition from "./SacredPageTransition";

import {
  FloatingTabBar,
} from "./FloatingTabBar";

import MorningSplash from "./MorningSplash";


import SuspensionGuard from "./SuspensionGuard";
import FeedbackWidget from "../components/FeedbackWidget";
/* =========================================================
   ONBOARDING BANNER
========================================================= */

function OnboardingBanner() {
  const navigate = useNavigate();

  const location = useLocation();

  const { t } = useI18n();

  const onboarded = storage.get(
    StorageKeys.OnboardingComplete,
    false
  );

  const [left, setLeft] = useState("");


  useEffect(() => {
    if (onboarded) return;


    let first = storage.get(
      "gs_first_visit",
      0
    );


    if (!first) {
      first = Date.now();

      storage.set(
        "gs_first_visit",
        first
      );
    }


    const tick = () => {
      const rem =
        3600000 -
        (Date.now() - Number(first));


      if (rem <= 0) {
        setLeft("00:00");

        return;
      }


      const m = Math.floor(
        rem / 60000
      );

      const s = Math.floor(
        (rem % 60000) / 1000
      );


      setLeft(
        `${String(m).padStart(
          2,
          "0"
        )}:${String(s).padStart(
          2,
          "0"
        )}`
      );
    };


    tick();


    const iv = setInterval(
      tick,
      1000
    );


    return () => {
      clearInterval(iv);
    };
  }, [onboarded]);


  if (
    onboarded ||
    location.pathname ===
      "/onboarding"
  ) {
    return null;
  }


  return (
    <button
      onClick={() =>
        navigate("/onboarding")
      }
      data-testid="onboarding-banner"
      className="
        w-full
        flex
        items-center
        justify-center
        gap-2
        px-4
        py-2.5
        text-center
        text-xs
        sm:text-sm
        font-semibold
        text-white
      "
      style={{
        background:
          "linear-gradient(90deg,#C6272C,#E5484D)",
      }}
    >
      🙏 {t("Almost there Make GurSetu Yours Sangat Ji! Just a few seconds to complete your profile")}·{""}
      {left || "60:00"}
    </button>
  );
}


/* =========================================================
   TYPING TAGLINE
========================================================= */

const TYPING_LINES = [
  "Every line of code is written with Chardi Kala.",
  "Every pixel is designed with love.",
  "Made with ❤️ for the Sangat",
  "Built by Codesphere Technologies ❤️.",
];


function TypingTagline() {
  const [
    lineIndex,
    setLineIndex,
  ] = useState(0);


  const [
    text,
    setText,
  ] = useState("");


  const [
    isDeleting,
    setIsDeleting,
  ] = useState(false);


  const [
    isPaused,
    setIsPaused,
  ] = useState(false);


  useEffect(() => {
    const currentLine =
      TYPING_LINES[lineIndex];


    let timeout;


    if (isPaused) {
      timeout = setTimeout(() => {
        setIsPaused(false);

        setIsDeleting(true);
      }, 5000);


      return () =>
        clearTimeout(timeout);
    }


    if (
      !isDeleting &&
      text === currentLine
    ) {
      setIsPaused(true);

      return;
    }


    if (
      isDeleting &&
      text === ""
    ) {
      setIsDeleting(false);


      setLineIndex(
        (prev) =>
          (prev + 1) %
          TYPING_LINES.length
      );


      return;
    }


    const speed =
      isDeleting ? 25 : 40;


    timeout = setTimeout(() => {
      setText(
        isDeleting
          ? currentLine.substring(
              0,
              text.length - 1
            )
          : currentLine.substring(
              0,
              text.length + 1
            )
      );
    }, speed);


    return () =>
      clearTimeout(timeout);
  }, [
    text,
    isDeleting,
    lineIndex,
    isPaused,
  ]);


  return (
    <span
      className="
        text-[10px]
        tracking-[1.5px]
        text-muted
        uppercase
        inline-flex
        items-center
      "
    >
      <span className="text-gold mr-1.5">
        ੴ
      </span>


      <span className="min-h-[14px]">
        {text}
      </span>


      <span
        className="
          ml-1
          w-[2px]
          h-3
          bg-gold
          animate-pulse
        "
      />
    </span>
  );
}


/* =========================================================
   STATIC SIDEBAR WORDMARK
   IMPORTANT:
   - No Framer Motion
   - No remounting
   - No repeating animation
   - Used ONLY inside desktop sidebar + mobile drawer
   - Main app header can still use GurSetuWordmark normally
========================================================= */

function StaticSidebarWordmark({
  size = "sm",
  className = "",
}) {
  const isMedium =
    size === "md";

  return (
    <div
      className={`
        flex
        flex-col
        items-center
        justify-center
        select-none
        ${className}
      `}
      style={{
        WebkitBackfaceVisibility:
          "hidden",
        backfaceVisibility:
          "hidden",
        transform:
          "translateZ(0)",
      }}
      data-testid="static-sidebar-wordmark"
    >
      {/* STATIC ੴ */}
      <span
        className="
          block
          font-bold
          text-amber-400
        "
        style={{
          fontSize:
            isMedium
              ? 24
              : 18,

          lineHeight: 1.15,

          textShadow:
            "0 0 12px rgba(251,191,36,0.12)",
        }}
      >
        ੴ
      </span>


      {/* STATIC GURSETU TEXT */}
      <span
        className="
          block
          whitespace-nowrap
          font-black
          tracking-[0.2px]
          text-amber-400
        "
        style={{
          fontSize:
            isMedium
              ? 34
              : 26,

          lineHeight: 1,

          textShadow:
            "0 0 10px rgba(251,191,36,0.08)",
        }}
      >
        GurSetu
      </span>


      {/* STATIC GOLD HAIRLINE */}
      <div
        className="
          mt-1.5
          flex
          items-center
          justify-center
          gap-1.5
        "
        aria-hidden="true"
      >
        <span
          className="
            h-px
            w-8
            bg-gradient-to-l
            from-amber-400
            to-transparent
          "
        />

        <span
          className="
            h-[3px]
            w-[3px]
            rounded-full
            bg-amber-400
          "
        />

        <span
          className="
            h-px
            w-8
            bg-gradient-to-r
            from-amber-400
            to-transparent
          "
        />
      </div>
    </div>
  );
}


/* =========================================================
   DESKTOP BRAND
   STATIC WORDMARK INSIDE SIDEBAR
========================================================= */

function Brand({
  compact = false,
}) {
  return (
    <Link
      to="/"
      className="
        flex
        items-center
        gap-3
      "
      data-testid="brand-logo"
    >
      <img
        src="/assets/images/icon.png"
        alt="GurSetu logo"
        draggable={false}
        className="
          w-10
          h-10
          rounded-xl
          object-cover

          border
          border-[var(--gold-subtle)]

          shrink-0
        "
        style={{
          WebkitBackfaceVisibility:
            "hidden",

          backfaceVisibility:
            "hidden",
        }}
      />


      {!compact && (
        <div
          className="
            min-w-0
            flex
            flex-col
            leading-none
          "
        >
          {/* NO ANIMATED WORDMARK IN SIDEBAR */}
          <StaticSidebarWordmark
            size="sm"
            className="items-start"
          />


          {/* Existing tagline kept unchanged */}
          <div
            className="
              mt-1
              h-4
              flex
              items-center
            "
          >
            <TypingTagline />
          </div>
        </div>
      )}
    </Link>
  );
}


/* =========================================================
   MOBILE DRAWER HEADER
   STATIC WORDMARK — ZERO LOGO ANIMATION
========================================================= */

function MobileDrawerHeader() {
  return (
    <div
      className="
        flex
        flex-col
        items-center

        py-3

        border-b
        border-[var(--glass-border)]
      "
    >
      <div className="mb-1">
        {/* NO GurSetuWordmark HERE */}
        <StaticSidebarWordmark
          size="md"
        />
      </div>


      {/* Existing tagline kept unchanged */}
      <div
        className="
          h-4
          flex
          items-center
        "
      >
        <TypingTagline />
      </div>
    </div>
  );
}


/* =========================================================
   SIDEBAR LANGUAGE COPY
   EN / HI / PA
   Only sidebar-facing labels use this map.
========================================================= */

const SIDEBAR_TEXT = {
  en: {
    sections: {
      Daily: "Daily",
      Community: "Community",
      Journal: "Journal",
      "Coming Soon": "Coming Soon",
      More: "More",
    },

    items: {
      home: "Home",
      nitnem: "Nitnem",
      hukamnama: "Hukamnama",
      simran: "Simran",

      inspiration: "Guru Inspiration",
      guruinspiration: "Guru Inspiration",

      events: "Events",
      gallery: "Gallery",
      kids: "Kids Mode",
      quest: "Sikh Quest",

      yatra: "Digital Yatra",
      digitalyatra: "Digital Yatra",

      "hukam-journal": "Hukam Journal",
      hukamjournal: "Hukam Journal",

      bookmarks: "Bookmarks",

      ai: "AI Sahayak",
      aisahayak: "AI Sahayak",

      kirtan: "Live Kirtan",
      livekirtan: "Live Kirtan",

      sevasetu: "SevaSetu",
      "one-min": "One Minute with Waheguru",
      gurcast: "GurCast",

      calendar: "Calendar",
      settings: "Settings",
      feedback: "Feedback & Queries",
      about: "About",
    },

    current: "Current",
    newBadge: "New",
    soonBadge: "Soon",
    swipeUp: "Swipe up",
    swipeDown: "Swipe down",
    expand: "Expand sidebar",
    collapse: "Collapse sidebar",
    latest: "Latest from GurSetu",
  },

  hi: {
    sections: {
      Daily: "दैनिक",
      Community: "संगत",
      Journal: "जर्नल",
      "Coming Soon": "जल्द आ रहा है",
      More: "और",
    },

    items: {
      home: "होम",
      nitnem: "नितनेम",
      hukamnama: "हुकमनामा",
      simran: "सिमरन",

      inspiration: "गुरु प्रेरणा",
      guruinspiration: "गुरु प्रेरणा",

      events: "कार्यक्रम",
      gallery: "गैलरी",
      kids: "बच्चों का मोड",
      quest: "सिख क्वेस्ट",

      yatra: "डिजिटल यात्रा",
      digitalyatra: "डिजिटल यात्रा",

      "hukam-journal": "हुकम जर्नल",
      hukamjournal: "हुकम जर्नल",

      bookmarks: "बुकमार्क्स",

      ai: "AI सहायक",
      aisahayak: "AI सहायक",

      kirtan: "लाइव कीर्तन",
      livekirtan: "लाइव कीर्तन",

      sevasetu: "SevaSetu",
      "one-min": "वाहेगुरु के साथ एक मिनट",
      gurcast: "GurCast",

      calendar: "कैलेंडर",
      settings: "सेटिंग्स",
      feedback: "फीडबैक और प्रश्न",
      about: "हमारे बारे में",
    },

    current: "वर्तमान",
    newBadge: "नया",
    soonBadge: "जल्द",
    swipeUp: "ऊपर स्वाइप करें",
    swipeDown: "नीचे स्वाइप करें",
    expand: "साइडबार खोलें",
    collapse: "साइडबार छोटा करें",
    latest: "GurSetu से नवीनतम",
  },

  pa: {
    sections: {
      Daily: "ਰੋਜ਼ਾਨਾ",
      Community: "ਸੰਗਤ",
      Journal: "ਜਰਨਲ",
      "Coming Soon": "ਜਲਦੀ ਆ ਰਿਹਾ ਹੈ",
      More: "ਹੋਰ",
    },

    items: {
      home: "ਹੋਮ",
      nitnem: "ਨਿਤਨੇਮ",
      hukamnama: "ਹੁਕਮਨਾਮਾ",
      simran: "ਸਿਮਰਨ",

      inspiration: "ਗੁਰੂ ਪ੍ਰੇਰਣਾ",
      guruinspiration: "ਗੁਰੂ ਪ੍ਰੇਰਣਾ",

      events: "ਸਮਾਗਮ",
      gallery: "ਗੈਲਰੀ",
      kids: "ਬੱਚਿਆਂ ਦਾ ਮੋਡ",
      quest: "ਸਿੱਖ ਕਵੇਸਟ",

      yatra: "ਡਿਜ਼ਿਟਲ ਯਾਤਰਾ",
      digitalyatra: "ਡਿਜ਼ਿਟਲ ਯਾਤਰਾ",

      "hukam-journal": "ਹੁਕਮ ਜਰਨਲ",
      hukamjournal: "ਹੁਕਮ ਜਰਨਲ",

      bookmarks: "ਬੁੱਕਮਾਰਕਸ",

      ai: "AI ਸਹਾਇਕ",
      aisahayak: "AI ਸਹਾਇਕ",

      kirtan: "ਲਾਈਵ ਕੀਰਤਨ",
      livekirtan: "ਲਾਈਵ ਕੀਰਤਨ",

      sevasetu: "SevaSetu",
      "one-min": "ਵਾਹਿਗੁਰੂ ਨਾਲ ਇੱਕ ਮਿੰਟ",
      gurcast: "GurCast",

      calendar: "ਕੈਲੰਡਰ",
      settings: "ਸੈਟਿੰਗਾਂ",
      feedback: "ਫੀਡਬੈਕ ਅਤੇ ਸਵਾਲ",
      about: "ਸਾਡੇ ਬਾਰੇ",
    },

    current: "ਮੌਜੂਦਾ",
    newBadge: "ਨਵਾਂ",
    soonBadge: "ਜਲਦੀ",
    swipeUp: "ਉੱਪਰ ਸਵਾਈਪ ਕਰੋ",
    swipeDown: "ਹੇਠਾਂ ਸਵਾਈਪ ਕਰੋ",
    expand: "ਸਾਈਡਬਾਰ ਖੋਲ੍ਹੋ",
    collapse: "ਸਾਈਡਬਾਰ ਛੋਟਾ ਕਰੋ",
    latest: "GurSetu ਤੋਂ ਨਵੀਂ ਜਾਣਕਾਰੀ",
  },
};


/* =========================================================
   MENU LIST
========================================================= */

function MenuList({
  onNavigate,
}) {

  const navigate =
    useNavigate();


  const location =
    useLocation();


  const {
    t,
    lang,
  } = useI18n();


  const sidebarText =
    SIDEBAR_TEXT[
      lang
    ] ||
    SIDEBAR_TEXT.en;


const go = (item) => {
  if (item.disabled) return;

  // ✅ Feedback — navigate nahi karna, modal kholna hai
  if (item.key === "feedback" || item.route === "#feedback") {
    onNavigate?.();   // drawer band karo (mobile)
    window.dispatchEvent(new CustomEvent("gursetu:open-feedback"));
    return;
  }

  if (item.route === "/kirtan") { navigate("/kirtan"); onNavigate?.(); return; }
  navigate(item.route);
  onNavigate?.();
};
 


  const getImageForItem = (
    key
  ) => {
    const imageMap = {
      home: "home.png",

      nitnem: "nitnem.png",

      hukamnama:
        "hukamnama.png",

      simran: "simran.png",

      guruinspiration:
        "guruInspiration.png",

      events: "events.png",

      gallery: "gallery.png",

      kids: "kids.png",

      quest: "quest.png",

      digitalyatra:
        "yatra.png",

      hukamjournal:
        "hukamjournal.png",

      bookmarks:
        "bookmarks.png",

      aisahayak:
        "aiSahayak1.png",

      livekirtan:
        "liveKirtan.png",

      settings:
        "settings.png",

      about: "about.png",
       feedback: "feedback.png", 

       getgursetu:
  "gursetu-logo.png",
    };


    return (
      imageMap[
        key.toLowerCase()
      ] || "home.png"
    );
  };


  return (
    <nav
      className="
        flex
        flex-col
        gap-5
        pb-4
      "
    >
      {MENU.map((sec) => (
        <div
          key={sec.section}
        >
          {/* SECTION TITLE */}

          <div
            className="
              flex
              items-center
              gap-3
              px-4
              mb-2
            "
          >
            <div
              className="
                h-px
                flex-1
                bg-[var(--gold-subtle)]
              "
            />


            <span
              className="
                text-[10px]
                font-extrabold
                tracking-[1.5px]
                text-gold
              "
            >
              {(
                sidebarText.sections[
                  sec.section
                ] ||
                sec.section
              ).toUpperCase()}
            </span>


            <div
              className="
                h-px
                flex-1
                bg-[var(--gold-subtle)]
              "
            />
          </div>


          {sec.items.map(
            (item) => {
              const imageName =
                getImageForItem(
                  item.key
                );


              const imagePath =
                `/assets/icons/${imageName}`;


              return (
                <button
                  key={item.key}

                  onClick={() =>
                    go(item)
                  }

                  disabled={
                    item.disabled
                  }

                  data-testid={`sidebar-${item.key}`}

                  className={`
                    w-full
                    flex
                    items-center
                    gap-3

                    px-4
                    py-3
                    mx-1

                    rounded-2xl

                    transition-all
                    duration-200

                    text-left

                    ${
                      item.disabled
                        ? "opacity-45 cursor-not-allowed"
                        : "hover:bg-[rgba(212,175,55,0.12)] active:scale-[0.98]"
                    }
                  `}
                >
                  {/* ICON */}

                  <div
                    className="
                      w-11
                      h-11

                      rounded-xl

                      grid
                      place-items-center

                      border
                      border-[var(--gold-subtle)]

                      bg-[rgba(212,175,55,0.08)]

                      overflow-hidden

                      flex-shrink-0
                    "
                  >
                    <img
                      src={
                        imagePath
                      }

                      alt={
                        item.title
                      }

                      className="
                        w-8
                        h-8
                        object-contain
                      "

                      onError={(
                        e
                      ) => {
                        e.currentTarget.style.display =
                          "none";
                      }}
                    />
                  </div>


                  {/* TITLE */}

                  <span
                    className="
                      flex-1
                      text-[15px]
                      font-medium
                      text-text
                    "
                  >
                    {sidebarText.items[
                      item.key
                    ] ||
                      item.title}
                  </span>


                  {/* BADGE */}

                  {item.badge ? (
                    <span
                      className={`
                        text-[9px]
                        font-extrabold

                        px-2
                        py-0.5

                        rounded-full

                        ${
                          item.badge ===
                          "Soon"
                            ? "border border-[var(--gold-subtle)] text-gold"
                            : "gold-gradient text-black"
                        }
                      `}
                    >
                      {item.badge ===
                      "NEW"
                        ? sidebarText.newBadge
                        : item.badge ===
                          "Soon"
                        ? sidebarText.soonBadge
                        : item.badge}
                    </span>
                  ) : null}


                  <ChevronRight
                    size={16}

                    className="
                      text-gold
                      opacity-70
                    "
                  />
                </button>
              );
            }
          )}
        </div>
      ))}
    </nav>
  );
}


/* =========================================================
   LATEST ADMIN EVENT CARD
========================================================= */

function LatestAdminEventCard({
  event,
  onOpen,
}) {

  const {
    lang,
  } = useI18n();


  const sidebarText =
    SIDEBAR_TEXT[
      lang
    ] ||
    SIDEBAR_TEXT.en;


  if (!event) {
    return null;
  }


  return (
    <div
      className="
        mx-4
        mt-5
        mb-8
      "
    >
      {/* TITLE DIVIDER */}

      <div
        className="
          flex
          items-center
          gap-2
          mb-2.5
          px-1
        "
      >
        <div
          className="
            h-px
            flex-1

            bg-amber-500/20

            dark:bg-amber-300/15
          "
        />


        <span
          className="
            whitespace-nowrap

            text-[9px]

            font-extrabold

            tracking-[1.7px]

            uppercase

            text-amber-700/70

            dark:text-amber-300/70
          "
        >
          {sidebarText.latest}
        </span>


        <div
          className="
            h-px
            flex-1

            bg-amber-500/20

            dark:bg-amber-300/15
          "
        />
      </div>


      {/* EVENT CARD */}

      <motion.button
        type="button"

        whileTap={{
          scale: 0.985,
        }}

        onClick={onOpen}

        className="
          group

          relative

          w-full

          overflow-hidden

          rounded-[18px]

          border
          border-amber-400/30

          dark:border-amber-300/20

          bg-[#fffdf8]

          dark:bg-[#101722]

          px-3
          py-3

          text-left

          shadow-[0_8px_24px_rgba(15,23,42,0.08)]

          dark:shadow-[0_10px_28px_rgba(0,0,0,0.32)]

          transition-all
          duration-300

          hover:border-amber-500/50

          dark:hover:border-amber-300/35
        "

        aria-label={`Open latest update: ${
          event.title ||
          "GurSetu update"
        }`}
      >
        {/* GLOW */}

        <div
          className="
            pointer-events-none

            absolute

            -right-7
            -top-7

            h-24
            w-24

            rounded-full

            bg-amber-300/15

            blur-2xl

            dark:bg-amber-400/10
          "
        />


        {/* GOLD LINE */}

        <div
          className="
            pointer-events-none

            absolute

            inset-x-0
            top-0

            h-px

            bg-gradient-to-r

            from-transparent

            via-amber-400/50

            to-transparent

            dark:via-amber-300/30
          "
        />


        <div
          className="
            relative
            z-10

            flex
            items-center
            gap-3
          "
        >
          {/* BELL */}

          <div
            className="
              relative

              grid

              h-9
              w-9

              shrink-0

              place-items-center

              rounded-xl

              border
              border-amber-400/30

              dark:border-amber-300/20

              bg-amber-50

              dark:bg-amber-400/10
            "
          >
            <Bell
              size={16}

              className="
                text-amber-700

                dark:text-amber-300
              "
            />


            {event.priority ===
              "urgent" && (
              <span
                className="
                  absolute

                  -right-1
                  -top-1

                  h-2.5
                  w-2.5

                  rounded-full

                  border-2

                  border-[#fffdf8]

                  dark:border-[#101722]

                  bg-red-500

                  animate-pulse
                "
              />
            )}
          </div>


          {/* INFO */}

          <div
            className="
              min-w-0
              flex-1
            "
          >
            <div
              className="
                mb-1

                flex

                min-w-0

                items-center

                gap-2
              "
            >
              <span
                className="
                  max-w-[130px]

                  truncate

                  rounded-full

                  border

                  border-amber-400/20

                  dark:border-amber-300/15

                  bg-amber-100/80

                  dark:bg-amber-300/10

                  px-2
                  py-0.5

                  text-[8px]

                  font-black

                  uppercase

                  tracking-[1px]

                  text-amber-800

                  dark:text-amber-300
                "
              >
                {event.category ||
                  "Event"}
              </span>


              {event.priority ===
                "urgent" && (
                <span
                  className="
                    text-[8px]

                    font-black

                    uppercase

                    tracking-[0.8px]

                    text-red-600

                    dark:text-red-400
                  "
                >
                  Urgent
                </span>
              )}
            </div>


            {/* EVENT TITLE */}

            <p
              className="
                truncate

                text-[13px]

                font-bold

                leading-5

                text-slate-900

                dark:text-slate-100
              "
            >
              {event.title ||
                "New GurSetu update"}
            </p>


            {/* BODY */}

            {event.body && (
              <p
                className="
                  mt-0.5

                  line-clamp-1

                  text-[10.5px]

                  leading-4

                  text-slate-600

                  dark:text-slate-400
                "
              >
                {event.body}
              </p>
            )}


            {/* DATE */}

            {event.createdAt && (
              <div
                className="
                  mt-1.5

                  flex

                  items-center

                  gap-1

                  text-[9px]

                  font-medium

                  text-slate-400

                  dark:text-slate-500
                "
              >
                <Clock
                  size={9}
                />


                <span>
                  {String(
                    event.createdAt
                  ).slice(0, 10)}
                </span>
              </div>
            )}
          </div>


          <ChevronRight
            size={15}

            className="
              shrink-0

              text-amber-600/70

              dark:text-amber-300/65

              transition-transform

              duration-300

              group-hover:translate-x-0.5
            "
          />
        </div>
      </motion.button>
    </div>
  );
}



/* =========================================================
   IN-APP APP UPDATE POPUP
   - Appears only after MorningSplash is gone AND onboarding is complete.
   - Maximum 2 impressions per IST calendar day.
   - Minimum 12 hours between two impressions.
   - "I've updated" suppresses this version permanently for that user/device.
   - No Continue/Later action: the popup is intentionally focused on the update.
========================================================= */

const GURSETU_UPDATE_VERSION = "2026.10.03";
const GURSETU_UPDATE_URL =
  "https://play.google.com/store/apps/details?id=com.gursetu.app";
const GURSETU_UPDATE_STATE_KEY =
  "gursetu_app_update_popup_v1";
const GURSETU_UPDATE_MAX_PER_DAY = 2;
const GURSETU_UPDATE_GAP_MS = 12 * 60 * 60 * 1000;

const UPDATE_POPUP_COPY = {
  en: {
    eyebrow: "GurSetu Update",
    title: "A better GurSetu is here",
    body:
      "Get the latest app experience with new improvements, feature upgrades, important bug fixes and a smoother Android experience.",
    features: "What’s improved",
    feature1: "New features & feature upgrades",
    feature2: "Important bug fixes",
    feature3: "Smoother, cleaner mobile experience",
    update: "Get Latest Update",
    updated: "I’ve updated — don’t show again",
    secure: "Official GurSetu app update",
  },
  hi: {
    eyebrow: "GurSetu अपडेट",
    title: "GurSetu का नया अपडेट तैयार है",
    body:
      "नए सुधार, फीचर अपग्रेड, ज़रूरी bug fixes और बेहतर Android अनुभव के लिए GurSetu का नवीनतम अपडेट पाएँ।",
    features: "क्या बेहतर हुआ है",
    feature1: "नए फीचर्स और फीचर अपग्रेड",
    feature2: "ज़रूरी bug fixes",
    feature3: "ज़्यादा smooth और clean mobile experience",
    update: "नवीनतम अपडेट पाएँ",
    updated: "मैंने अपडेट कर लिया — दोबारा न दिखाएँ",
    secure: "Official GurSetu app update",
  },
  pa: {
    eyebrow: "GurSetu ਅੱਪਡੇਟ",
    title: "GurSetu ਦਾ ਨਵਾਂ ਅੱਪਡੇਟ ਤਿਆਰ ਹੈ",
    body:
      "ਨਵੇਂ ਸੁਧਾਰ, ਫੀਚਰ ਅੱਪਗ੍ਰੇਡ, ਜ਼ਰੂਰੀ bug fixes ਅਤੇ ਹੋਰ smooth Android experience ਲਈ ਨਵਾਂ GurSetu ਅੱਪਡੇਟ ਲਵੋ।",
    features: "ਕੀ ਬਿਹਤਰ ਹੋਇਆ ਹੈ",
    feature1: "ਨਵੇਂ ਫੀਚਰ ਅਤੇ ਫੀਚਰ ਅੱਪਗ੍ਰੇਡ",
    feature2: "ਜ਼ਰੂਰੀ bug fixes",
    feature3: "ਹੋਰ smooth ਅਤੇ clean mobile experience",
    update: "ਨਵਾਂ ਅੱਪਡੇਟ ਲਵੋ",
    updated: "ਮੈਂ ਅੱਪਡੇਟ ਕਰ ਲਿਆ — ਮੁੜ ਨਾ ਦਿਖਾਓ",
    secure: "Official GurSetu app update",
  },
};

function getIstDateKey(value = new Date()) {
  return new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(value);
}

function readUpdatePopupState() {
  const raw = storage.get(
    GURSETU_UPDATE_STATE_KEY,
    {}
  );

  if (!raw || typeof raw !== "object") {
    return {
      date: getIstDateKey(),
      shownCount: 0,
      lastShownAt: 0,
      acknowledgedVersion: "",
    };
  }

  const today = getIstDateKey();

  if (raw.date !== today) {
    return {
      date: today,
      shownCount: 0,
      lastShownAt: 0,
      acknowledgedVersion:
        raw.acknowledgedVersion || "",
    };
  }

  return {
    date: today,
    shownCount: Number(raw.shownCount) || 0,
    lastShownAt: Number(raw.lastShownAt) || 0,
    acknowledgedVersion:
      raw.acknowledgedVersion || "",
  };
}

function writeUpdatePopupState(next) {
  storage.set(
    GURSETU_UPDATE_STATE_KEY,
    next
  );
}

function AppUpdatePopup({
  open,
  language,
  onLanguageChange,
  onUpdated,
  onGetUpdate,
}) {
  if (!open) {
    return null;
  }

  const copy =
    UPDATE_POPUP_COPY[language] ||
    UPDATE_POPUP_COPY.en;

  const stopEvent = (event) => {
    event.stopPropagation();
  };

  return (
    <div
      className="fixed inset-0 z-[2147483646] flex items-center justify-center px-4 py-5"
      role="dialog"
      aria-modal="true"
      aria-labelledby="gursetu-update-title"
      data-testid="gursetu-update-popup"
      onPointerDown={stopEvent}
      onTouchMove={(event) => {
        event.preventDefault();
        event.stopPropagation();
      }}
      style={{
        touchAction: "none",
        isolation: "isolate",
      }}
    >
      {/* Stable backdrop: blur + dark veil, but no animated GPU-heavy layer. */}
      <div
        className="absolute inset-0 bg-black/62 backdrop-blur-[9px]"
        aria-hidden="true"
      />

      <motion.div
        initial={{ opacity: 0, y: 10, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
        onPointerDown={stopEvent}
        className="relative z-10 w-full max-w-[360px] overflow-hidden rounded-[26px] border border-amber-300/25 bg-[#0a1119] text-white shadow-[0_24px_90px_rgba(0,0,0,.62)]"
        style={{
          WebkitBackfaceVisibility: "hidden",
          backfaceVisibility: "hidden",
          transform: "translateZ(0)",
        }}
      >
        <div className="absolute inset-x-8 top-0 h-px bg-gradient-to-r from-transparent via-amber-300/80 to-transparent" />

        <div className="px-4 pb-4 pt-4 sm:px-5 sm:pt-5">
          {/* Header */}
          <div className="flex items-start gap-3">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-[15px] border border-amber-300/25 bg-amber-400/10 text-amber-300 shadow-[0_8px_24px_rgba(245,158,11,.10)]">
              <Sparkles size={21} strokeWidth={2.1} />
            </div>

            <div className="min-w-0 flex-1">
              <p className="text-[9px] font-black uppercase tracking-[0.14em] text-amber-300/80">
                {copy.eyebrow}
              </p>
              <h2
                id="gursetu-update-title"
                className="mt-1 text-[18px] font-black leading-[1.18] text-white"
              >
                {copy.title}
              </h2>
            </div>

            {/* Explicit language control for new users too. */}
            <select
              value={language}
              onChange={(event) =>
                onLanguageChange(event.target.value)
              }
              aria-label="Choose update language"
              className="h-9 w-[62px] shrink-0 rounded-full border border-white/10 bg-white/[0.06] px-2 text-[10px] font-black text-white outline-none"
            >
              <option value="en">EN</option>
              <option value="hi">हिं</option>
              <option value="pa">ਪੰ</option>
            </select>
          </div>

          <p className="mt-3 text-[12px] leading-[1.6] text-white/62">
            {copy.body}
          </p>

          {/* Improvement cards */}
          <div className="mt-4 rounded-[18px] border border-white/8 bg-white/[0.035] p-3">
            <p className="text-[9px] font-black uppercase tracking-[0.12em] text-amber-300/75">
              {copy.features}
            </p>

            <div className="mt-2.5 grid gap-2">
              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-amber-400/10 text-amber-300">
                  <Sparkles size={14} />
                </span>
                <span className="text-[11px] font-semibold leading-4 text-white/82">
                  {copy.feature1}
                </span>
              </div>

              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-emerald-400/10 text-emerald-300">
                  <Bug size={14} />
                </span>
                <span className="text-[11px] font-semibold leading-4 text-white/82">
                  {copy.feature2}
                </span>
              </div>

              <div className="flex items-center gap-2.5">
                <span className="grid h-8 w-8 shrink-0 place-items-center rounded-[10px] bg-sky-400/10 text-sky-300">
                  <Smartphone size={14} />
                </span>
                <span className="text-[11px] font-semibold leading-4 text-white/82">
                  {copy.feature3}
                </span>
              </div>
            </div>
          </div>

          {/* Primary Play Store CTA */}
          <a
            href={GURSETU_UPDATE_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={onGetUpdate}
            className="mt-4 inline-flex min-h-[48px] w-full items-center justify-center gap-2 rounded-[15px] bg-gradient-to-r from-[#d8a94f] to-[#f0ca78] px-4 text-[12px] font-black text-[#11161d] shadow-[0_10px_28px_rgba(216,169,79,.18)] active:scale-[0.985]"
          >
            <Download size={16} strokeWidth={2.4} />
            {copy.update}
          </a>

          {/* Explicit acknowledgement */}
          <button
            type="button"
            onClick={onUpdated}
            className="mt-2.5 inline-flex min-h-[42px] w-full items-center justify-center gap-2 rounded-[13px] border border-white/10 bg-white/[0.035] px-3 text-[10px] font-extrabold text-white/72 active:scale-[0.99]"
          >
            <CheckCircle2 size={14} className="text-emerald-300" />
            {copy.updated}
          </button>

          <div className="mt-3 flex items-center justify-center gap-1.5 text-[8px] font-semibold text-white/32">
            <CheckCircle2 size={10} className="text-emerald-300/60" />
            {copy.secure}
          </div>
        </div>
      </motion.div>
    </div>
  );
}


/* =========================================================
   MAIN LAYOUT
========================================================= */

export default function Layout({
  children,
}) {
  const [
    drawer,
    setDrawer,
  ] = useState(false);


  /*
   * false = normal compact drawer
   * true  = expanded / almost full screen
   */
  const [
    drawerExpanded,
    setDrawerExpanded,
  ] = useState(false);


  const [
    latestEvent,
    setLatestEvent,
  ] = useState(null);
  const [
  hasUnreadNotification,
  setHasUnreadNotification,
] = useState(false);

  /* =========================================================
     APP UPDATE POPUP STATE
  ========================================================= */
  const [
    showUpdatePopup,
    setShowUpdatePopup,
  ] = useState(false);

  const [
    updatePopupLang,
    setUpdatePopupLang,
  ] = useState("en");

  const updateWaitTimerRef =
    React.useRef(null);

  const updateClosedByAcknowledgeRef =
    React.useRef(false);

  /* Keep the page completely inert while the update dialog is open. */
  useEffect(() => {
    if (
      !showUpdatePopup ||
      typeof document === "undefined"
    ) {
      return;
    }

    const previousOverflow =
      document.body.style.overflow;
    const previousTouchAction =
      document.body.style.touchAction;
    const previousOverscroll =
      document.body.style.overscrollBehaviorY;

    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
    document.body.style.overscrollBehaviorY = "none";

    return () => {
      document.body.style.overflow = previousOverflow;
      document.body.style.touchAction = previousTouchAction;
      document.body.style.overscrollBehaviorY = previousOverscroll;
    };
  }, [showUpdatePopup]);


  const location =
    useLocation();


  const navigate =
    useNavigate();

const isAIPage =
  location.pathname === "/ai";

  const {
  t,
  lang,
} = useI18n();


const sidebarText =
  SIDEBAR_TEXT[
    lang
  ] ||
  SIDEBAR_TEXT.en;


const AI_BACK_LABELS = {
  en: "Back Home",
  hi: "होम वापस",
  pa: "ਹੋਮ ਵਾਪਸ",
};

const aiBackLabel =
  AI_BACK_LABELS[lang] ||
  AI_BACK_LABELS.en;

  /*
   * IMPORTANT:
   * dragControls allows us to start dragging ONLY
   * from the handle at the top.
   *
   * Isse menu ke andar normal scrolling disturb nahi hogi.
   */
  const dragControls =
    useDragControls();


  /* =========================================================
     OPEN DRAWER
  ========================================================= */

  const openDrawer = () => {
    setDrawerExpanded(false);

    setDrawer(true);
  };


  /* =========================================================
     CLOSE DRAWER
  ========================================================= */

  const closeDrawer = () => {
    setDrawer(false);


    setTimeout(() => {
      setDrawerExpanded(false);
    }, 250);
  };


  /* =========================================================
     LATEST EVENT FETCH
  ========================================================= */

const fetchLatestEvent =
  useCallback(async () => {
    try {
      const r =
        await api.get(
          "/announcements?limit=1"
        );

      if (
        r?.items?.length > 0
      ) {
        const newest =
          r.items[0];

        setLatestEvent(
          newest
        );

        const seenId =
          storage.get(
            "gs_last_seen_notification_id",
            ""
          );

        setHasUnreadNotification(
          String(seenId) !==
            String(newest.id)
        );
      } else {
        setLatestEvent(
          null
        );

        setHasUnreadNotification(
          false
        );
      }
    } catch (error) {
      console.error(
        "Failed to load latest admin event:",
        error
      );
    }
  }, []);


  /* =========================================================
     LOAD EVENT ON APP START
  ========================================================= */

  useEffect(() => {
    fetchLatestEvent();
  }, [fetchLatestEvent]);
  useEffect(() => {
  const refreshNotifications = () => {
    if (
      document.visibilityState === "visible"
    ) {
      fetchLatestEvent();
    }
  };

  window.addEventListener(
    "focus",
    fetchLatestEvent
  );

  document.addEventListener(
    "visibilitychange",
    refreshNotifications
  );

  return () => {
    window.removeEventListener(
      "focus",
      fetchLatestEvent
    );

    document.removeEventListener(
      "visibilitychange",
      refreshNotifications
    );
  };
}, [fetchLatestEvent]);


  /* =========================================================
     FETCH LATEST EVENT EVERY TIME DRAWER OPENS
  ========================================================= */

  useEffect(() => {
    if (drawer) {
      fetchLatestEvent();
    }
  }, [
    drawer,
    fetchLatestEvent,
  ]);



  /* =========================================================
     APP UPDATE POPUP — AFTER SPLASH + ONBOARDING
  ========================================================= */
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }

    if (typeof React.useRef !== "function") {
      return;
    }

    const isReadyForUpdatePopup = () => {
      const onboardingDone = !!storage.get(
        StorageKeys.OnboardingComplete,
        false
      );

      if (!onboardingDone) return false;
      if (location.pathname === "/onboarding") return false;
      if (drawer) return false;
      if (document.visibilityState !== "visible") return false;

      const splash = document.querySelector(
        '[data-testid="morning-splash"]'
      );

      if (splash) return false;

      return true;
    };

    const maybeShowUpdatePopup = () => {
      if (!isReadyForUpdatePopup()) return;
      if (updateClosedByAcknowledgeRef.current) return;
      if (showUpdatePopup) return;

      const state = readUpdatePopupState();

      if (
        state.acknowledgedVersion ===
        GURSETU_UPDATE_VERSION
      ) {
        return;
      }

      if (
        state.shownCount >=
        GURSETU_UPDATE_MAX_PER_DAY
      ) {
        return;
      }

      if (
        state.lastShownAt &&
        Date.now() - state.lastShownAt <
          GURSETU_UPDATE_GAP_MS
      ) {
        return;
      }

      writeUpdatePopupState({
        ...state,
        shownCount: state.shownCount + 1,
        lastShownAt: Date.now(),
        date: getIstDateKey(),
      });

      setUpdatePopupLang(
        ["en", "hi", "pa"].includes(lang)
          ? lang
          : "en"
      );
      setShowUpdatePopup(true);
    };

    const scheduleCheck = (delay = 1000) => {
      if (updateWaitTimerRef.current) {
        window.clearTimeout(
          updateWaitTimerRef.current
        );
      }

      updateWaitTimerRef.current =
        window.setTimeout(() => {
          maybeShowUpdatePopup();
        }, delay);
    };

    const handleOnboardingComplete = () => {
      scheduleCheck(900);
    };

    const handleVisible = () => {
      if (document.visibilityState === "visible") {
        scheduleCheck(900);
      }
    };

    const handleFocus = () => {
      scheduleCheck(900);
    };

    const handlePageshow = () => {
      scheduleCheck(1200);
    };

    window.addEventListener(
      "gursetu:onboarding-complete",
      handleOnboardingComplete
    );
    document.addEventListener(
      "visibilitychange",
      handleVisible
    );
    window.addEventListener(
      "focus",
      handleFocus
    );
    window.addEventListener(
      "pageshow",
      handlePageshow
    );

    // Initial check: this waits until MorningSplash has actually left the DOM.
    scheduleCheck(1700);

    // Re-check when the drawer closes; no popup should stack on top of it.
    return () => {
      if (updateWaitTimerRef.current) {
        window.clearTimeout(
          updateWaitTimerRef.current
        );
        updateWaitTimerRef.current = null;
      }

      window.removeEventListener(
        "gursetu:onboarding-complete",
        handleOnboardingComplete
      );
      document.removeEventListener(
        "visibilitychange",
        handleVisible
      );
      window.removeEventListener(
        "focus",
        handleFocus
      );
      window.removeEventListener(
        "pageshow",
        handlePageshow
      );
    };
  }, [
    drawer,
    location.pathname,
    lang,
    showUpdatePopup,
  ]);

  const acknowledgeAppUpdate = useCallback(() => {
    const state = readUpdatePopupState();

    writeUpdatePopupState({
      ...state,
      acknowledgedVersion:
        GURSETU_UPDATE_VERSION,
      date: getIstDateKey(),
    });

    updateClosedByAcknowledgeRef.current = true;
    setShowUpdatePopup(false);
  }, []);

  const openLatestAppUpdate = useCallback(() => {
    try {
      window.dispatchEvent(
        new CustomEvent("gursetu:app-update-click", {
          detail: {
            version: GURSETU_UPDATE_VERSION,
          },
        })
      );
    } catch (_) {}
  }, []);

  const openNotifications = () => {

  if (
    latestEvent?.id
  ) {
    storage.set(
      "gs_last_seen_notification_id",
      String(
        latestEvent.id
      )
    );
  }

  /*
   * Pehle amber dot dissolve.
   * Phir page open.
   */

  setHasUnreadNotification(
    false
  );

  window.setTimeout(
    () => {
      navigate(
        "/notifications"
      );
    },
    280
  );
};

useEffect(() => {

  /*
   * User notification page ko
   * sidebar ya kisi aur route se
   * open kare tab bhi unread clear.
   */

  if (
    location.pathname !==
      "/notifications" ||
    !latestEvent?.id
  ) {
    return;
  }

  storage.set(
    "gs_last_seen_notification_id",
    String(
      latestEvent.id
    )
  );

  setHasUnreadNotification(
    false
  );

}, [
  location.pathname,
  latestEvent?.id,
]);

  /* =========================================================
     LOCK PAGE SCROLL WHILE DRAWER OPEN
  ========================================================= */

  useEffect(() => {
    if (
      !drawer ||
      typeof document ===
        "undefined"
    ) {
      return;
    }


    const oldOverflow =
      document.body.style.overflow;


    document.body.style.overflow =
      "hidden";


    return () => {
      document.body.style.overflow =
        oldOverflow;
    };
  }, [drawer]);


  /* =========================================================
     DRAWER STATE -> HOME / WEBVIEW COMPOSITOR
     Home pauses expensive decorative motion while the drawer
     is visible. This prevents the Home-only Android WebView
     flicker / layer-cut glitch.
  ========================================================= */

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof document === "undefined"
    ) {
      return;
    }

    const root = document.documentElement;

    if (drawer) {
      root.classList.add("gursetu-drawer-open");
    } else {
      root.classList.remove("gursetu-drawer-open");
    }

    window.dispatchEvent(
      new CustomEvent("gursetu:drawer-state", {
        detail: { open: drawer },
      })
    );

    return () => {
      root.classList.remove("gursetu-drawer-open");
    };
  }, [drawer]);


  /* =========================================================
     ESC KEY CLOSE
  ========================================================= */

  useEffect(() => {
    if (!drawer) return;


    const handleKeyDown = (
      event
    ) => {
      if (
        event.key ===
        "Escape"
      ) {
        closeDrawer();
      }
    };


    window.addEventListener(
      "keydown",
      handleKeyDown
    );


    return () => {
      window.removeEventListener(
        "keydown",
        handleKeyDown
      );
    };
  }, [drawer]);


  /* =========================================================
     CLOSE AFTER ROUTE CHANGE
  ========================================================= */

  useEffect(() => {
    setDrawer(false);

    setDrawerExpanded(false);
  }, [location.pathname]);

/* =========================================================
   GLOBAL MOBILE KEYBOARD NAVIGATION FIX
========================================================= */

useEffect(() => {
  if (
    typeof window === "undefined" ||
    typeof document === "undefined"
  ) {
    return;
  }

  const root =
    document.documentElement;

  /*
   * AI Sahayak has its own visualViewport keyboard
   * controller. Do NOT apply the generic keyboard
   * class there, otherwise two systems fight and
   * the composer can jump below/above the keyboard.
   */
  if (isAIPage) {
    root.classList.remove(
      "gursetu-keyboard-open"
    );

    return;
  }

  const isEditable =
    (element) => {
      if (!element) {
        return false;
      }

      const tag =
        element.tagName;

      return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        tag === "SELECT" ||
        element.isContentEditable
      );
    };

  const handleFocusIn =
    (event) => {
      if (
        window.innerWidth < 1024 &&
        isEditable(event.target)
      ) {
        root.classList.add(
          "gursetu-keyboard-open"
        );
      }
    };

  const handleFocusOut =
    () => {
      setTimeout(() => {
        if (
          !isEditable(
            document.activeElement
          )
        ) {
          root.classList.remove(
            "gursetu-keyboard-open"
          );
        }
      }, 80);
    };

  document.addEventListener(
    "focusin",
    handleFocusIn
  );

  document.addEventListener(
    "focusout",
    handleFocusOut
  );

  return () => {
    document.removeEventListener(
      "focusin",
      handleFocusIn
    );

    document.removeEventListener(
      "focusout",
      handleFocusOut
    );

    root.classList.remove(
      "gursetu-keyboard-open"
    );
  };
}, [isAIPage]);
  /* =========================================================
     DRAG / SWIPE END
  ========================================================= */

  const handleDrawerDragEnd = (
    event,
    info
  ) => {
    const offsetY =
      info.offset.y;


    const velocityY =
      info.velocity.y;


    /*
     * SWIPE DOWN HARD:
     * Close drawer completely
     */

    if (
      offsetY > 120 ||
      velocityY > 900
    ) {
      closeDrawer();

      return;
    }


    /*
     * SWIPE UP:
     * Expand drawer almost full screen
     */

    if (
      offsetY < -55 ||
      velocityY < -650
    ) {
      setDrawerExpanded(
        true
      );

      return;
    }


    /*
     * Drawer expanded hai aur
     * user thoda neeche swipe kare,
     * toh pehle compact position.
     */

    if (
      drawerExpanded &&
      offsetY > 45
    ) {
      setDrawerExpanded(
        false
      );

      return;
    }
  };


  /* =========================================================
     ADMIN PAGE
  ========================================================= */

  const isAdmin =
    location.pathname.startsWith(
      "/admin"
    );
   
    const isSettingsPage =
  location.pathname === "/settings";


  if (isAdmin) {
    return <>{children}</>;
  }


  return (
    <div
      className={`
        relative
        min-h-screen
        min-h-[100dvh]
        overflow-x-hidden
        bg-app

        ${
          isAIPage
            ? "h-[100dvh] overflow-y-hidden"
            : ""
        }
      `}
    >
      {/* BACKGROUND */}

      <div
        className="
          fixed
          inset-0
          pointer-events-none
        "

        style={{
          background:
            "radial-gradient(1200px 600px at 80% -10%, rgba(212,175,55,0.10), transparent 60%), radial-gradient(900px 500px at -10% 20%, rgba(212,175,55,0.06), transparent 55%)",
        }}
      />


      {/* =====================================================
          DESKTOP SIDEBAR
      ===================================================== */}

      <aside
        className="
          hidden
          lg:flex

          fixed

          top-0
          left-0

          h-screen

          w-[280px]

          flex-col

          border-r

          border-[var(--glass-border)]

          bg-[var(--bg-soft)]

          z-40
        "
      >
        <div
          className="
            p-5

            border-b

            border-[var(--glass-border)]
          "
        >
          <Brand />
        </div>


        <div
          className="
            flex-1

            overflow-y-auto

            no-scrollbar

            pt-3
          "
        >
          <MenuList />
        </div>


        <div
          className="
            p-4

            border-t

            border-[var(--glass-border)]

            text-center
          "
        >
          <p
            className="
              text-gold
              text-xs
              font-bold
            "
          >
            Made with ❤️ for the
            Sangat
          </p>
        </div>
      </aside>


      {/* =====================================================
          MAIN CONTENT
      ===================================================== */}

      <div
        className={`
          lg:ml-[280px]

          relative

          z-10

          ${
            isAIPage
              ? "flex h-[100dvh] min-h-0 flex-col overflow-hidden"
              : ""
          }
        `}
      >
         


        {/* HEADER */}

        <header
          className="
            sticky

            top-0

            z-30

            shrink-0

            flex

            items-center

            gap-2
px-3
py-3

min-[390px]:gap-3
min-[390px]:px-4

            border-b

            border-[var(--glass-border)]

            glass

            lg:justify-end
          "
        >
          {/* MOBILE MENU */}
{/* MOBILE NAVIGATION */}
{/* MOBILE NAVIGATION */}

{isAIPage ? (

  <motion.button
    type="button"

    onClick={() =>
      navigate("/")
    }

    whileTap={{
      scale: 0.94,
    }}

    animate={{
      x: [0, -2, 0],
    }}

    transition={{
      x: {
        duration: 1.8,
        repeat: Infinity,
        ease: "easeInOut",
      },
    }}

    className="
      lg:hidden

      h-11
     w-[92px]
min-[390px]:w-[112px]

      shrink-0

      flex
      items-center
      justify-center

      gap-1.5

      rounded-full

      border
      border-[var(--gold-subtle)]

      bg-[var(--bg-soft)]

      px-2

      text-gold

      shadow-sm

      active:scale-95
    "

    aria-label={aiBackLabel}

    data-testid="ai-back-home"
  >

    <ArrowLeft
      size={18}
      strokeWidth={2.5}
      className="shrink-0"
    />

    <span
      className="
        whitespace-nowrap
        text-[9px]
min-[390px]:text-[10px]
        font-extrabold
        leading-none
      "
    >
      {aiBackLabel}
    </span>

  </motion.button>

) : isSettingsPage ? (

  <button
    type="button"

    onClick={() =>
      navigate("/")
    }

    className="
      lg:hidden
      w-11
      h-11
      rounded-full
      grid
      place-items-center
      btn-ghost
      text-text
      active:scale-95
    "

    data-testid="settings-back-home"

    aria-label="Back to Home"
  >

    <ArrowLeft
      size={21}
      strokeWidth={2.2}
    />

  </button>

) : (

  <button
    type="button"

    onClick={openDrawer}

    className="
      lg:hidden
      w-11
      h-11
      rounded-full
      grid
      place-items-center
      btn-ghost
      text-text
      active:scale-95
    "

    data-testid="hamburger-btn"

    aria-label="Open menu"
  >

    <Menu size={20} />

  </button>

)}

          {/* MOBILE LOGO */}

          <div
            data-testid="mobile-header-logo"
            className="
              mobile-header-logo
              flex-1

              flex

              justify-center

              lg:hidden
            "
          >
            <GurSetuWordmark
              size="sm"
            />
          </div>


          {/* NOTIFICATION */}

          {/* NOTIFICATION */}

{!isAIPage ? (

  <motion.button
    type="button"

    onClick={
      openNotifications
    }

    whileTap={{
      scale: 0.92,
    }}

    className="
      relative

      grid

      h-11
      w-11

      shrink-0

      place-items-center

      rounded-full

      btn-ghost
    "

    data-testid="notif-bell"

    aria-label="Notifications"
  >

    {/* ==============================
        SMART BELL SWING
    ============================== */}

    <motion.div

      key={
        hasUnreadNotification
          ? `unread-${
              latestEvent?.id ||
              "notification"
            }`
          : "bell-read"
      }

      initial={{
        rotate: 0,
      }}

      animate={
        hasUnreadNotification
          ? {
              rotate: [
                0,
                -11,
                9,
                -6,
                4,
                0,
              ],
            }
          : {
              rotate: 0,
            }
      }

      transition={{
        duration: 1.05,
        delay: 0.18,
        ease: "easeInOut",
      }}

      style={{
        transformOrigin:
          "50% 8%",
      }}
    >

      <Bell
        size={19}
      />

    </motion.div>


    {/* ==============================
        UNREAD AMBER DOT
    ============================== */}

    <AnimatePresence>

      {hasUnreadNotification && (

       <motion.span
  key={`notification-dot-${latestEvent?.id || "new"}`}

  initial={{
    opacity: 0,
    scale: 0,
  }}

  animate={{
    opacity: [1, 0.55, 1],
    scale: [1, 1.22, 1],
  }}

  exit={{
    opacity: 0,
    scale: 1.8,
    filter: "blur(5px)",

    transition: {
      duration: 0.28,
      ease: "easeOut",
    },
  }}

  transition={{
    opacity: {
      duration: 1.6,
      repeat: Infinity,
      ease: "easeInOut",
    },

    scale: {
      duration: 1.6,
      repeat: Infinity,
      ease: "easeInOut",
    },
  }}

  className="
    absolute

    right-[9px]
    top-[8px]

    h-[7px]
    w-[7px]

    rounded-full

    border
    border-[var(--bg-soft)]

    bg-amber-400

    shadow-[0_0_8px_rgba(251,191,36,0.85)]
  "
/>

      )}

    </AnimatePresence>

  </motion.button>

) : (
  <div
  className="
    h-11

    w-[92px]
    min-[390px]:w-[112px]

    shrink-0

    lg:hidden
  "
  aria-hidden="true"
/>
)}
        </header>


        {/* PAGE */}

         <main
  className={`
    w-full
    min-w-0
    max-w-5xl

    mx-auto

    px-3
    min-[390px]:px-4
    sm:px-6

    pt-3

    ${
      isAIPage
        ? "min-h-0 flex-1 overflow-hidden pb-2 lg:pb-6"
        : "pb-[calc(112px+env(safe-area-inset-bottom))] lg:pb-12"
    }
  `}
>
          <SacredPageTransition>
  {children}
</SacredPageTransition>
        </main>
      </div>


      {/* BOTTOM NAV */}

     {/* BOTTOM NAV */}

{!isAIPage && !drawer && (
  <FloatingTabBar />
)}

<SuspensionGuard />
      {/* MORNING SPLASH */}
<FeedbackWidget />
      <MorningSplash />
      <OnboardingWrapper />

      <AppUpdatePopup
        open={showUpdatePopup}
        language={updatePopupLang}
        onLanguageChange={(value) => {
          if (["en", "hi", "pa"].includes(value)) {
            setUpdatePopupLang(value);
          }
        }}
        onUpdated={acknowledgeAppUpdate}
        onGetUpdate={openLatestAppUpdate}
      />

      {/* =====================================================
          POWERFUL MOBILE DRAWER
      ===================================================== */}

      <AnimatePresence>
        {drawer && (
          <>
            {/* BACKDROP — opaque enough to isolate Home GPU layers */}

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={closeDrawer}
              className="
                fixed
                inset-0
                z-[60]
                lg:hidden
                bg-[#02050a]/82
                dark:bg-[#02050a]/88
              "
              style={{
                WebkitBackfaceVisibility: "hidden",
                backfaceVisibility: "hidden",
                WebkitTransform: "translateZ(0)",
                transform: "translateZ(0)",
              }}
              data-testid="drawer-backdrop"
            />


            {/* =================================================
                DRAWER
            ================================================= */}

            <motion.div
             initial={{
  y: "100%",
  opacity: 1,
}}

animate={{
  y: 0,
  opacity: 1,
}}

exit={{
  y: "100%",
  opacity: 1,
}}

transition={{
  duration: 0.28,
  ease: [0.22, 1, 0.36, 1],
}}
              drag="y"

              dragControls={
                dragControls
              }

              dragListener={
                false
              }

              dragConstraints={{
                top: 0,
                bottom: 0,
              }}

              dragElastic={{
                top: 0.14,
                bottom: 0.32,
              }}

              onDragEnd={
                handleDrawerDragEnd
              }

               
                className={`
  fixed
  bottom-0 left-0 right-0
  ${drawerExpanded ? "h-[94dvh]" : "h-[78dvh]"}
  max-h-[94dvh]
  rounded-t-[32px]
  bg-[#fffdf8]
  dark:bg-[#07111b]
  z-[70]
  lg:hidden
  flex
  flex-col
  overflow-hidden
  border-t
  border-[var(--gold-subtle)]
  shadow-[0_-24px_65px_rgba(0,0,0,0.24)]
  dark:shadow-[0_-28px_80px_rgba(0,0,0,0.72)]
  transition-[height]
  duration-300
  ease-out
`}
style={{
  isolation: "isolate",
  contain: "layout paint",
  overscrollBehavior: "contain",
  WebkitBackfaceVisibility: "hidden",
  backfaceVisibility: "hidden",
  WebkitTransform: "translateZ(0)",
  transform: "translateZ(0)",
  willChange: "transform",
}}
data-testid="mobile-drawer"
>

              {/* STATIC GOLD HAIRLINE — no continuous motion */}
              <div
                className="
                  pointer-events-none
                  absolute
                  inset-x-8
                  top-0
                  z-[6]
                  h-px
                  bg-gradient-to-r
                  from-transparent
                  via-amber-400/45
                  to-transparent
                "
                aria-hidden="true"
              />


              {/* =================================================
                  DRAG AREA
                  USER YAHAN SE UP/DOWN DRAG KAREGA
              ================================================= */}

              <div
                onPointerDown={(
                  event
                ) => {
                  dragControls.start(
                    event
                  );
                }}

                className="
                  relative

                  flex-shrink-0

                  cursor-grab

                  active:cursor-grabbing

                  touch-none

                  select-none

                  pt-2

                  pb-1
                "
              >
                {/* SOFT GOLD TOP GLOW */}

                <div
                  className="
                    pointer-events-none

                    absolute

                    top-0

                    left-1/2

                    -translate-x-1/2

                    w-[65%]

                    h-10

                    bg-[rgba(212,175,55,0.08)]

                    blur-2xl
                  "
                />


                {/* ANIMATED ARROW */}

                <div
                  className="
                    flex

                    flex-col

                    items-center

                    justify-center

                    gap-0.5
                  "
                >
                  <div className="text-gold">
                    {drawerExpanded ? (
                      <ChevronDown size={18} />
                    ) : (
                      <ChevronUp size={18} />
                    )}
                  </div>


                  {/* DRAG HANDLE */}

                  <div
                    className="
                      w-12

                      h-[5px]

                      rounded-full

                      bg-[rgba(212,175,55,0.38)]

                      shadow-[0_0_12px_rgba(245,158,11,0.20)]
                    "
                  />


                  <span
                    className="
                      mt-1

                      text-[8px]

                      uppercase

                      tracking-[1.4px]

                      font-bold

                      text-muted
                    "
                  >
                    {drawerExpanded
                      ? sidebarText.swipeDown
                      : sidebarText.swipeUp}
                  </span>
                </div>
              </div>


              {/* =================================================
                  ARROW TAP BUTTON
              ================================================= */}

              <button
                type="button"

                onClick={() =>
                  setDrawerExpanded(
                    (prev) =>
                      !prev
                  )
                }

                className="
                  absolute

                  top-3

                  right-5

                  z-20

                  w-8

                  h-8

                  rounded-full

                  grid

                  place-items-center

                  border

                  border-[var(--gold-subtle)]

bg-[#fffaf0]

dark:bg-[#0c1722]

text-gold

                  transition-all

                  active:scale-90
                "

                aria-label={
                  drawerExpanded
                    ? sidebarText.collapse
                    : sidebarText.expand
                }
              >
                <motion.div
                  animate={{
                    rotate:
                      drawerExpanded
                        ? 180
                        : 0,
                  }}

                  transition={{
                    duration: 0.25,
                  }}
                >
                  <ChevronUp
                    size={16}
                  />
                </motion.div>
              </button>


              {/* BRAND */}

              <MobileDrawerHeader />


              {/* FATEH */}

              <div
                className="
                  mx-5

                  mb-2

                  py-3

                  px-4

                  rounded-2xl

                  border

                  border-[var(--gold-subtle)]

                  bg-[rgba(212,175,55,0.08)]

                  text-center

                  flex-shrink-0
                "
              >
                <p
                  className="
                    text-gold
                    text-xs
                    font-semibold
                  "
                >
                  {t("fateh")}
                </p>


                <p
                  className="
                    text-gold
                    text-xs
                    font-semibold
                  "
                >
                  {t(
                    "fatehReply"
                  )}
                </p>
              </div>


              {/* =================================================
                  SCROLLABLE SIDEBAR CONTENT
              ================================================= */}

              <div
                className="
                  flex-1
                  min-h-0
                  overflow-y-auto
                  overscroll-contain
                  no-scrollbar
                  touch-pan-y
                  pb-[max(24px,env(safe-area-inset-bottom))]
                "
                style={{
                  WebkitOverflowScrolling: "touch",
                }}
              >
                {/* ALL MENU OPTIONS */}

                <MenuList
                  onNavigate={
                    closeDrawer
                  }
                />


                {/* ===============================================
                    LATEST ADMIN EVENT
                    ALWAYS AT LAST
                =============================================== */}

               <LatestAdminEventCard
  event={latestEvent}

  onOpen={() => {
    closeDrawer();
    openNotifications();
  }}
/>


                {/* SMALL END SPACER */}

                <div
                  className="
                    h-3
                  "
                />
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
      
    
 
    </div>
  );
}